import defaultPictureSmall from "assets/images/profile-pic-placeholder-sm.svg";
import { Button, Icon, Spinner } from "components";
import { useFullName } from "hooks";
import { FC, useCallback, useMemo, useState } from "react";
import { USER_ROLE_STATUS } from "types/TeamUsersResponse";
import { generateMediaPath } from "utils";

import { ANALYTICS_EVENTS } from "constants/analyticEvents";
import { FEATURES, FEATURE_ACTION } from "constants/features";
import { useUserState } from "context";
import { useCheckPermission, useIsActionAllowed, useTracking } from "hooks";
import { useUpgradeModal } from "hooks/useUpgradeModal";
import { tokens } from "@ovou-inc/ovou-ui/tokens";
import { useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import { MAIN_SITE } from "../../constants";
import { useTeamUsersResendMutation } from "./api/useTeamUsersMutation";
import { IProfileNameCol } from "./types";

const ProfileNameCol: FC<IProfileNameCol> = ({
  row,
  setSelectedUserToEdit,
}) => {
  const { addToast } = useToasts();
  const track = useTracking();
  const { currentTeam } = useUserState();
  const teamID = currentTeam?.id as number;
  const {
    email,
    status,
    first_name,
    last_name,
    id,
    user_iam_role,
    profile,
    user_role,
  } = row;
  const { picture = "", handle } = profile || {};
  const isWaiting = user_role?.status === USER_ROLE_STATUS.APPOINTED;
  const fullName = useFullName(row, row.profile);
  const { openModal } = useUpgradeModal();
  const queryCache = useQueryCache();
  const { mutate: fireResendInvitation } = useTeamUsersResendMutation();
  const [isResending, setIsResending] = useState(false);

  const hasInvitationExpired = useMemo(() => {
    return Boolean(
      isWaiting &&
      user_role?.invitation_expires_at &&
      new Date(user_role.invitation_expires_at) < new Date(),
    );
  }, [isWaiting, user_role]);

  const canEditChangeInvitation = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_USERS_CHANGE_EMAIL,
  );
  const canEditResendInvitation = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_USERS_RESEND_EMAIL,
  );
  const { handleAction } = useIsActionAllowed();

  const handleResendInvitation = useCallback(() => {
    handleAction(
      canEditResendInvitation,
      FEATURES.MANAGE_USERS_RESEND_EMAIL,
      () => {
        setIsResending(true);
        fireResendInvitation(
          {
            teamId: teamID,
            userId: id,
          },
          {
            onSuccess: async () => {
              await queryCache.invalidateQueries([
                `team-users`,
                { teamId: teamID },
              ]);
              addToast("Invitation sent successfully.", {
                appearance: "success",
              });
              track({
                eventName: ANALYTICS_EVENTS.MANAGE_USERS_RESEND_INVITE,
                customTrackingProps: {
                  userId: id,
                  userEmail: email,
                },
              });
              setIsResending(false);
            },
            onError: error => {
              addToast(error as string, {
                appearance: "error",
              });
              setIsResending(false);
            },
          },
        );
      },
      openModal,
    );
  }, [
    canEditResendInvitation,
    teamID,
    id,
    email,
    openModal,
    track,
    queryCache,
  ]);

  const handleChangeInvitation = useCallback(() => {
    handleAction(
      canEditChangeInvitation,
      FEATURES.MANAGE_USERS_CHANGE_EMAIL,
      () => {
        setSelectedUserToEdit({
          id,
          email,
          first_name,
          last_name,
          status,
          user_iam_role,
        });
        track({
          eventName: ANALYTICS_EVENTS.MANAGE_USERS_OPEN_CHANGE_INVITATION_MODAL,
        });
      },
      openModal,
    );
  }, [canEditChangeInvitation, id, openModal, track]);

  return (
    <div className="flex items-center w-full">
      {isWaiting ? (
        <div
          style={{ minHeight: "64px" }}
          className="flex items-center justify-center w-12"
        >
          <Icon
            name="clock"
            color={
              hasInvitationExpired ? tokens.color.feedback.error : undefined
            }
          />
        </div>
      ) : (
        <div>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${MAIN_SITE}/${handle}`}
          >
            <img
              className="rounded-xl w-12 h-16 block"
              src={picture ? generateMediaPath(picture) : defaultPictureSmall}
              alt={`${fullName}'s Profile Pic`}
            />
          </a>
        </div>
      )}
      <div className="ml-6 flex items-end">
        {isWaiting ? (
          <span className="text-black font-semibold">
            <span
              style={
                hasInvitationExpired
                  ? { color: tokens.color.feedback.error }
                  : undefined
              }
            >
              {hasInvitationExpired ? "Invitation Expired" : "Waiting for"}
            </span>
            <br /> {email}
          </span>
        ) : (
          <div>
            {fullName?.trim() && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${MAIN_SITE}/${handle}`}
                className="inline-flex"
              >
                <span className="block text-black text-lg font-bold">
                  {fullName}
                </span>
              </a>
            )}
            {email && (
              <span className="block text-custom-gray-500 text-sm mt-1">
                {email}
              </span>
            )}
          </div>
        )}
        {isWaiting && (
          <>
            <Button
              variant="white"
              size="small"
              className="pl-2 pr-2 pt-1 pb-1 ml-2 sm:text-xs"
              onClick={handleChangeInvitation}
              block
            >
              Change
            </Button>
            <Button
              block
              variant="white"
              size="small"
              className="pl-2 pr-2 pt-1 pb-1 ml-2 sm:text-xs"
              onClick={handleResendInvitation}
            >
              {isResending ? <Spinner size={5} /> : "Resend"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileNameCol;
